'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';
import { Country, getCountryCallingCode } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { useAnchoredMenu } from '@/hotdesk-ui/hooks/useAnchoredMenu';
import styles from './CountrySelect.module.scss';
import './CountrySelect.globals.scss';
type CountryOption = {
  label: string;
  value: Country | undefined;
};
interface CountrySelectProps {
  value?: Country;
  options: CountryOption[];
  onChange: (country: Country) => void;
  disabled?: boolean;
}
const FlagBox: FC<{
  country?: Country;
  title?: string;
}> = ({
  country,
  title
}) => {
  const Flag = country ? flags[country] : undefined;
  return <span className={styles.flag} data-sentry-component="FlagBox" data-sentry-source-file="CountrySelect.tsx">
            {Flag ? <Flag title={title || country || ''} /> : <span className={styles.flagFallback} />}
        </span>;
};
const CountrySelect: FC<CountrySelectProps> = ({
  value,
  options,
  onChange,
  disabled
}) => {
  const t = useTranslations('phone-input');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // The menu lines up with the whole phone field, not just the flag.
  const anchorRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items = options.filter(o => !!o.value);
    if (!q) return items;
    return items.filter(o => o.label.toLowerCase().includes(q) || o.value && `+${getCountryCallingCode(o.value as Country)}`.includes(q));
  }, [options, search]);
  const menuStyle = useAnchoredMenu(anchorRef, open, {
    placement: 'below'
  });
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);
  useEffect(() => {
    if (open) {
      setSearch('');
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);
  const selectedLabel = useMemo(() => options.find(o => o.value === value)?.label, [options, value]);
  const dialCode = value && options.some(o => o.value === value) ? getCountryCallingCode(value) : undefined;
  const menu = mounted && open && menuStyle ? <div ref={menuRef} className={styles.menu} role="listbox" style={menuStyle}>
                <div className={styles.search}>
                    <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search-placeholder')} className={styles.searchInput} />
                </div>
                <ul className={styles.list}>
                    {filteredOptions.length === 0 ? <li className={styles.empty}>{t('no-results')}</li> : filteredOptions.map(option => {
        const country = option.value as Country;
        const isSelected = country === value;
        return <li key={country} role="option" aria-selected={isSelected} className={`${styles.item} ${isSelected ? styles.itemSelected : ''}`.trim()} onClick={() => {
          onChange(country);
          setOpen(false);
        }}>
                                    <FlagBox country={country} title={option.label} />
                                    <span className={styles.label}>
                                        {option.label}
                                    </span>
                                    <span className={styles.code}>
                                        +{getCountryCallingCode(country)}
                                    </span>
                                </li>;
      })}
                </ul>
            </div> : null;
  return <>
            <button ref={triggerRef} type="button" className={styles.trigger} disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-label={selectedLabel || t('aria-label')} onClick={() => {
      if (disabled) return;
      anchorRef.current = triggerRef.current?.closest<HTMLElement>('.PhoneInput') ?? triggerRef.current;
      setOpen(v => !v);
    }}>
                <FlagBox country={value} title={selectedLabel} data-sentry-element="FlagBox" data-sentry-source-file="CountrySelect.tsx" />
                {dialCode && <span className={styles.dialCode}>+{dialCode}</span>}
                <ChevronDown size={14} strokeWidth={2} className={styles.chevron} aria-hidden="true" data-sentry-element="ChevronDown" data-sentry-source-file="CountrySelect.tsx" />
            </button>
            {mounted && menu && createPortal(menu, document.body)}
        </>;
};
export default CountrySelect;